import { Link, usePage } from '@inertiajs/react';
import classNames from 'classnames';
import { ReactNode } from 'react';
import { ExternalLink } from 'react-feather';
import { useTranslation } from 'react-i18next';

import { Charity, Project } from '../../../Shared/types';
import { Icon } from '../../../Shared/UI/Icon';
import { useRouter } from '../../router';
import { PageProps } from '../../types';
import Container from '../../UI/Container';
import Header from '../../UI/Header';
import { tabClasses } from '../../UI/helpers';
import { SettingsLayout } from '../SettingsLayout';
import { DefaultProjectLabel } from './DefaultProjectLabel';
import ProjectLogo from './ProjectLogo';
import ProjectToggle from './ProjectToggle';

export function ProjectLayout({
  children,
}: {
  children: ReactNode;
}) {
  const { props: { charity, project }, component } = usePage<PageProps<{ project: Project; }>>();

  return (
    <SettingsLayout>
      <ProjectHeader project={project} charity={charity} component={component} />
      {children}
    </SettingsLayout>
  );
}

export function ProjectHeader({
  project,
  charity,
  component,
}: {
  project: Project;
  charity: Charity;
  component: string;
}) {
  const { t } = useTranslation();
  const { routes } = useRouter();

  const showToggle = [
    'Backend/Settings/Projects/ProjectPage',
    'Backend/Settings/Projects/EditProjectPage',
  ].includes(component);

  return (
    <Header>
      <Container className="!pb-0">
        <div className="flex items-center justify-between w-full">
          <div className={classNames('flex items-center', !project?.enabled && 'opacity-50')}>
            <span className="mr-3">
              <ProjectLogo project={project} charity={charity} size="lg" />
            </span>
            <div>
              <h1>
                {project.title}
              </h1>
              {project.default && (
                <DefaultProjectLabel project={project} />
              )}
            </div>
          </div>

          {showToggle && (
            <div className="flex items-center">
              <div className="flex space-x-6">
                {project.url && (
                  <a
                    href={project.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={classNames(!project.visible && 'text-slate-400', 'font-medium')}
                    data-supporta-embed="false"
                  >
                    {t('backend:visit')}
                    <Icon className="ml-1">
                      <ExternalLink />
                    </Icon>
                  </a>
                )}
                <ProjectToggle project={project} />
              </div>
            </div>
          )}
        </div>

        <div className="mt-2 space-x-4">
          <Link
            href={routes.project_page(project.id)}
            className={tabClasses({ active: component === 'Backend/Settings/Projects/ProjectPage' })}
          >
            {t('backend:overview')}
          </Link>
          <Link
            href={routes.edit_project_page(project.id)}
            className={tabClasses({ active: component === 'Backend/Settings/Projects/EditProjectPage' })}
          >
            {t('backend:settings')}
          </Link>
          <Link
            href={routes.project_mails_page(project.id)}
            className={tabClasses({
              active: [
                'Backend/Settings/Projects/ProjectMailsPage',
                'Backend/Settings/Projects/EditProjectMailPage',
                'Backend/Settings/Projects/EditCustomMailPage',
              ].includes(component),
            })}
          >
            {t('backend:mails')}
          </Link>
        </div>
      </Container>
    </Header>
  );
}
